import React from "react";
import {
  Table,
  TableRow,
  TableCell,
  makeStyles,
  TableHead,
  TableBody,
  IconButton
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { TimereportExpenseDtoV2 } from "@api/timereports";
import { Edit, Delete, OpenInNew } from "@material-ui/icons";
import { Download } from "mdi-material-ui";
import { getReceiptPreviewUri } from "@bookings/BookingProfile/TimeReport/receipt-uri";
import { downloadReceipt } from "@bookings/BookingProfile/TimeReport/receipt-download";
import { useSnackbars } from "@hooks";

type Props = {
  mileage: string;
  lunch: number;
  expenses: TimereportExpenseDtoV2[];
  editBtn: React.ReactNode;
  comment: string;
  hideActions: boolean;
  editExpense: (index: number) => void;
  remExpense: (index: number) => void;
  consultantName: string;
  date: string;
};

export function TimereportDayInfo(props: Props) {
  const classes = useStyles({});
  const { showError } = useSnackbars();
  const hasAmount = props.expenses.some((x) => x.amount > 0);

  // A receipt is numbered across the whole day rather than within its expense, so two
  // expenses reported on the same day cannot both hand the browser a `kvitto-…-1`.
  const ordinals = new Map(
    props.expenses
      .flatMap((expense, e) => (expense.attachments ?? []).map((_, a) => `${e}.${a}`))
      .map((key, i) => [key, i + 1])
  );

  const saveReceipt = async (previewUri: string, expenseIndex: number, index: number) => {
    try {
      await downloadReceipt(previewUri, {
        date: props.date,
        consultantName: props.consultantName,
        ordinal: ordinals.get(`${expenseIndex}.${index}`) ?? index + 1
      });
    } catch {
      showError("Kvittot kunde inte laddas ner");
    }
  };

  const show = props.lunch > 0 || Number(props.mileage) > 0 || hasAmount || props.comment;
  return (
    show && (
      <Table className={classes.root} size={"small"}>
        <TableHead>
          {(props.lunch > 0 || Number(props.mileage) > 0 || props.comment.length) && (
            <TableRow>
              <TableCell>{locale.Lunch}</TableCell>
              <TableCell>{locale.Milage2}</TableCell>

              <TableCell>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {locale.Comment}
                </div>
              </TableCell>
              <TableCell>
                <span className={classes.btn}>{props.editBtn}</span>
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {(props.lunch > 0 || Number(props.mileage) > 0 || props.comment.length) && (
            <TableRow>
              <TableCell component={"th"} style={{ paddingTop: 6, whiteSpace: "nowrap" }}>
                {props.lunch} min
              </TableCell>
              <TableCell component={"th"} style={{ paddingTop: 6, whiteSpace: "nowrap" }}>
                {props.mileage} km
              </TableCell>
              <TableCell component={"th"} colSpan={2} style={{ paddingTop: 6 }}>
                {props.comment}
              </TableCell>
            </TableRow>
          )}
          {hasAmount && (
            <TableRow>
              <TableCell component={"th"} style={{ paddingTop: 10 }} colSpan={4}>
                {"Utlägg/kommentar"}
              </TableCell>
            </TableRow>
          )}
          {props.expenses.map(
            (item, index) =>
              item.amount > 0 && (
                <TableRow key={index}>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {item.amount} SEK
                  </TableCell>
                  <TableCell>{item.comment}</TableCell>
                  <TableCell>
                    <div className={classes.attachments}>
                      {item.attachments?.map(
                        (x, i) =>
                          x.uri && (
                            <React.Fragment key={i}>
                              <IconButton
                                size="small"
                                href={getReceiptPreviewUri(x.previewUri)}
                                target="_blank"
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => saveReceipt(x.previewUri, index, i)}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </React.Fragment>
                          )
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {!props.hideActions && (
                      <div style={{ display: "flex" }}>
                        <span className={classes.btn}>
                          <IconButton
                            size="small"
                            onClick={() => props.editExpense(index)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              props.remExpense(index);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )
          )}
        </TableBody>
      </Table>
    )
  );
}

const useStyles = makeStyles(() => ({
  cell: {
    display: "flex",
    flexDirection: "row"
  },
  root: {
    borderLeft: "1px solid #eee",

    "& td": {
      border: 0
    },
    "& th": {
      paddingTop: 0
    },

    "& td:first-child": {
      // paddingLeft: 0
    },
    "& button": {
      transition: "all .3s ease",
      opacity: 0
    },
    "&:hover": {
      cursor: "pointer",
      "& button": {
        opacity: 1
      }
    }
  },
  btn: {
    display: "inline-block",
    marginLeft: "auto"
  },
  attachments: {
    display: "flex",
    alignItems: "center",
    gap: 4,

    // The row fades its buttons in on hover, which suits the edit and delete actions it
    // owns. A receipt's own controls are the only way to reach the file at all, so they
    // stay visible.
    "&& button, && a": {
      opacity: 1
    }
  }
}));
